import { useEffect, useRef, useState } from 'react';
import { api, photoObjectUrl, type ResidencePhoto } from '../lib/api';
import { shrinkImage, sizeLabel } from '../lib/photos';

/**
 * Residence photographs (D73).
 *
 * One picture, fetched with the login attached and handed to the tag as an object URL, because
 * a browser sends no headers of its own for `<img src>`. The space it will occupy is reserved
 * from the stored size first, so a page with six pictures does not jump about as they land.
 */
function Photo({ residenceId, photo, onClick }: { residenceId: string; photo: ResidencePhoto; onClick?: () => void }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    let made: string | null = null;
    photoObjectUrl(residenceId, photo.id)
      .then((u) => {
        if (!live) return URL.revokeObjectURL(u);
        made = u;
        setUrl(u);
      })
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [residenceId, photo.id]);

  const ratio = photo.width && photo.height ? `${photo.width} / ${photo.height}` : undefined;
  return (
    <figure className="respic">
      <span className="respic-frame" style={ratio ? { aspectRatio: ratio } : undefined}>
        {failed ? (
          <span className="muted small">This picture couldn't be loaded.</span>
        ) : url ? (
          <img src={url} alt={photo.caption ?? 'Photograph of the residence'} onClick={onClick} className={onClick ? 'clickable' : undefined} />
        ) : (
          <span className="respic-loading" aria-hidden="true" />
        )}
      </span>
      {photo.caption && <figcaption>{photo.caption}</figcaption>}
    </figure>
  );
}

/** The pictures a physician sees on their travel page: read only, tap to enlarge. */
export function PhotoStrip({ residenceId, photos }: { residenceId: string; photos: ResidencePhoto[] }) {
  const [big, setBig] = useState<ResidencePhoto | null>(null);
  if (!photos.length) return null;
  return (
    <>
      <div className="respics">
        {photos.map((p) => (
          <Photo key={p.id} residenceId={residenceId} photo={p} onClick={() => setBig(p)} />
        ))}
      </div>
      {big && (
        <div className="overlay" onClick={() => setBig(null)} role="dialog" aria-label={big.caption ?? 'Photograph'}>
          <div className="respic-big" onClick={(e) => e.stopPropagation()}>
            <Photo residenceId={residenceId} photo={big} />
            <button type="button" onClick={() => setBig(null)}>Close</button>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * The coordinator's side: add a picture, caption it, take one away.
 *
 * The file is shrunk in this browser before it is sent — a phone camera's four megabytes
 * becomes a couple of hundred kilobytes, which is all a staircase needs and all a physician
 * on hospital wifi wants to download.
 */
export function PhotoManager({ residenceId, residenceName }: { residenceId: string; residenceName: string }) {
  const [photos, setPhotos] = useState<ResidencePhoto[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const picker = useRef<HTMLInputElement>(null);

  const load = () => api.residencePhotos(residenceId).then(setPhotos).catch(() => setPhotos([]));
  useEffect(() => { void load(); }, [residenceId]);

  const add = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true);
    setProblem(null);
    setNote(null);
    const done: string[] = [];
    try {
      for (const file of Array.from(files)) {
        const small = await shrinkImage(file);
        // The caption defaults to the file's own name, which is usually what somebody meant
        // ("front stairs.jpg"), and can be changed by removing and adding it again.
        const caption = file.name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').trim().slice(0, 200) || null;
        await api.addResidencePhoto(residenceId, { file: small.file, caption });
        done.push(`${caption ?? 'picture'} (${sizeLabel(small.bytes)})`);
      }
      setNote(`Added ${done.length} picture${done.length === 1 ? '' : 's'} to ${residenceName}: ${done.join(', ')}.`);
      await load();
    } catch (e) {
      setProblem(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      if (picker.current) picker.current.value = '';
    }
  };

  const remove = async (p: ResidencePhoto) => {
    setBusy(true);
    setProblem(null);
    try {
      await api.removeResidencePhoto(residenceId, p.id);
      await load();
    } catch (e) {
      setProblem(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="photomanager">
      <h4>Photographs</h4>
      <p className="muted small">
        How to find the door, and what each room is. A physician sees these from three days before they arrive until
        the day they leave — the same as the address. Pictures are shrunk before they are sent, so a photo straight
        off a phone is fine.
      </p>
      {photos === null && <p className="muted small">Loading…</p>}
      {photos?.length === 0 && <p className="muted small">No pictures yet.</p>}
      {photos && photos.length > 0 && (
        <div className="respics">
          {photos.map((p) => (
            <div key={p.id} className="respic-edit">
              <Photo residenceId={residenceId} photo={p} />
              <button type="button" className="linklike small danger" disabled={busy} onClick={() => remove(p)}>Remove</button>
            </div>
          ))}
        </div>
      )}
      <input ref={picker} type="file" accept="image/*" multiple disabled={busy} onChange={(e) => void add(e.target.files)} />
      {busy && <p className="muted small">Working…</p>}
      {note && <p className="small">{note}</p>}
      {problem && <p className="error small">{problem}</p>}
    </div>
  );
}
